import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import { useState, useCallback } from 'react';

import { useGetReportingTemplateDownloadUrlLazyQueryGQLType } from '../requests/reporting.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';

export const useDownloadReportTemplateUrl = (params: {
  creditFacilityId: string;
  templateConfig?: {
    name: string;
    s3Path: string;
  };
}) => {
  const { creditFacilityId, templateConfig } = params;

  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [
    getReportTemplateUrlQuery,
  ] = useGetReportingTemplateDownloadUrlLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: async ({ downloadUrl }) => {
      if (!templateConfig) {
        return;
      }

      const { name: templateName } = templateConfig;
      if (!downloadUrl) {
        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [template:${templateName}]`,
        });
        return;
      }

      try {
        const response = await axios({
          url: downloadUrl,
          method: 'GET',
          responseType: 'blob',
        });

        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });
        FileSaver.saveAs(fileBlob, templateName);
      } catch (_error) {
        toast({
          ...errorToastConfig,
          title: `Failed to download [template:${templateName}]`,
        });
      }
    },
  });

  const getReportTemplateDownload = useCallback(async () => {
    if (!templateConfig) {
      toast({
        ...errorToastConfig,
        title: 'Can not download non-existing template',
      });
      return;
    }

    setIsLoading(true);

    const { s3Path, name: templateName } = templateConfig;
    await getReportTemplateUrlQuery({
      variables: { creditFacilityId, s3Path, templateName },
    });

    setIsLoading(false);
  }, [creditFacilityId, getReportTemplateUrlQuery, templateConfig, toast]);

  return {
    getReportTemplateDownload,
    isLoading,
  };
};
